/* The six seats, and what each user calls them. The ids are what the data is made of — spot
   keys, `meta.hero`, the base sources — and never change; a name is what an id is shown as,
   and nothing else.

   The names are module state: core builds some thirty sentences that name a seat, and a
   naming function threaded through every one of their signatures would touch every caller
   for one display concern. Identity by default, set once when a page opens. */

export const SEATS = ["LJ", "HJ", "CO", "BTN", "SB", "BB"] as const;
export type Seat = (typeof SEATS)[number];

/** The seat pill on a phone is 68px wide and also holds the stack: "Button", "UTG+1" fit. */
export const SEAT_NAME_MAX = 6;

/** Only the seats that were renamed. */
export type SeatNames = Partial<Record<Seat, string>>;

let names: SeatNames = {};

const isSeat = (v: string): v is Seat => (SEATS as readonly string[]).includes(v);

/** What a seat is shown as. Anything that is not one of the six — a seat a user added to a
    picker, an empty string — is shown as it is. */
export const seatName = (seat: string): string => (isSeat(seat) && names[seat]) || seat;

/** Whatever was stored, by this build or another, or by hand: the entries that are names. A
    name equal to its seat's id is no renaming and is dropped. */
export function readSeatNames(raw: unknown): SeatNames {
  const out: SeatNames = {};
  if (!raw || typeof raw !== "object") return out;
  for (const [seat, value] of Object.entries(raw)) {
    if (!isSeat(seat) || typeof value !== "string") continue;
    const name = value.trim();
    if (name && name !== seat && name.length <= SEAT_NAME_MAX) out[seat] = name;
  }
  return out;
}

/** What is wrong with a set of names, in words, or null. Two seats shown under one name would
    make "MP vs MP" of a perfectly good spot, so the six names as shown must differ, whatever
    their case; a seat may take another's default only if that one gave it up. */
export function checkSeatNames(wanted: SeatNames): string | null {
  const shown = new Map<string, Seat>();
  for (const seat of SEATS) {
    const name = (wanted[seat] ?? "").trim() || seat;
    if (name.length > SEAT_NAME_MAX)
      return `"${name}" is too long for ${seat}: ${SEAT_NAME_MAX} characters at most.`;
    const taken = shown.get(name.toLowerCase());
    if (taken) return `${taken} and ${seat} would both be called "${name}".`;
    shown.set(name.toLowerCase(), seat);
  }
  return null;
}

/** Use these names from now on. What does not pass `readSeatNames` is left out, and a set that
    does not pass `checkSeatNames` is refused whole: the defaults are never ambiguous. */
export function setSeatNames(raw: unknown): SeatNames {
  const read = readSeatNames(raw);
  names = checkSeatNames(read) ? {} : read;
  return names;
}

export const currentSeatNames = (): SeatNames => ({ ...names });
